<template>
  <!--
    No loading or not-found branches. The page awaits its data during SSR, and a
    missing post throws a real 404 handled by error.vue — the previous
    "not found" block returned HTTP 200, i.e. a soft 404 on every bad slug.
    The v-if is only here so TypeScript can narrow `post`.
  -->
  <div v-if="post">
    <!-- Title band doubles as the page <h1>; the trail mirrors the BreadcrumbList JSON-LD below -->
    <SofaxBreadcrumb :title="cleanTitle(post.title)" :items="crumbs" />

    <section class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-[1.875rem] lg:grid-cols-[1fr_minmax(416px,0.5fr)]">
          <div>
            <article>
              <!-- Featured image -->
              <div v-if="post.image_url" class="mb-10 overflow-hidden rounded-[0.625rem] bg-surface dark:bg-gray-800">
                <NuxtImg
                  :src="post.image_url"
                  :alt="$t('blog.featuredImageAlt', { title: cleanTitle(post.title) })"
                  width="1296"
                  height="650"
                  sizes="sm:100vw lg:60vw"
                  class="h-auto w-full object-cover"
                  format="webp"
                  quality="80"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>

              <!-- Meta: category pill, date, read time -->
              <div class="mb-8 flex flex-wrap items-center gap-x-4 gap-y-3 text-base">
                <LocaleLink
                  v-if="post.category && categoryLabel"
                  :to="`/blog/category/${post.category}`"
                  class="btn btn-dark !min-w-0 !rounded-[5px] !px-4 !py-2 !text-base"
                >
                  <span>{{ categoryLabel }}</span>
                  <span aria-hidden="true">{{ categoryLabel }}</span>
                </LocaleLink>

                <time :datetime="post.published_at || post.created_at" class="text-body dark:text-gray-300">
                  {{ formatDate(post.published_at || post.created_at) }}
                </time>

                <span class="inline-flex items-center gap-1.5 text-muted dark:text-gray-400">
                  <i class="bi bi-clock" aria-hidden="true" />
                  {{ post.read_time_minutes }} {{ $t('blog.readTime') }}
                </span>
              </div>

              <!-- Standfirst -->
              <p v-if="post.description" class="lead mb-10">
                {{ post.description }}
              </p>

              <!-- Body: the design system's prose rules, no per-page overrides -->
              <div class="rich-text-block blog-content" v-html="post.content" />
            </article>

            <!-- The category's guide, so the article feeds that page rather than competing with it -->
            <div
              v-if="categoryGuide"
              class="mt-10 flex flex-col gap-4 rounded-[0.625rem] border-l-4 border-accent bg-surface p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6 dark:bg-gray-900"
            >
              <p class="!text-base text-body dark:text-gray-300">
                <strong class="text-ink dark:text-white">{{ $t('blog.guideNote.lead') }}</strong>
                {{ $t(categoryGuide.labelKey) }}
              </p>
              <SofaxArrowLink :to="categoryGuide.path" :label="$t('blog.guideNote.cta')" class="shrink-0" />
            </div>

            <PremiumJobPackNote class="mt-10" />

            <!-- Tags / share row -->
            <div class="my-10 flex flex-wrap items-center justify-between gap-x-10 gap-y-5 border-y border-line py-6 dark:border-gray-800">
              <div v-if="post.tags?.length" class="flex flex-wrap items-center gap-x-6 gap-y-3">
                <span class="display-heading display-heading-4">{{ $t('blog.tags') }}</span>
                <div class="flex flex-wrap gap-3">
                  <span v-for="tag in post.tags" :key="tag" class="chip">{{ tag }}</span>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
                <span class="display-heading display-heading-4">{{ $t('blog.share') }}</span>
                <div class="flex items-center gap-4">
                  <SofaxSocialLinks variant="dark" :share="{ url: canonical, title: cleanTitle(post.title) }" />
                  <button
                    type="button"
                    class="social-link social-link-dark"
                    :title="$t('blog.copyLink')"
                    :aria-label="$t('blog.copyLink')"
                    @click="copyLink"
                  >
                    <i class="bi bi-link-45deg" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Last updated + back link -->
            <div class="flex flex-wrap items-center justify-between gap-x-10 gap-y-4">
              <span class="!text-base text-muted dark:text-gray-400">
                {{ $t('blog.lastUpdated') }}
                <time :datetime="post.updated_at">{{ formatDate(post.updated_at) }}</time>
              </span>
              <SofaxArrowLink :to="home" :label="$t('blog.backToBlog')" />
            </div>
          </div>

          <BlogSidebar
            :recent-posts="recentPosts"
            :category="post.category"
            :locale="locale"
            @search="searchBlog"
          />
        </div>
      </div>
    </section>

    <SofaxPageCta />

    <!-- Sticky offer, client-only: its whole behaviour is scroll position. -->
    <ClientOnly>
      <BlogCtaBar :slug="post.slug" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { localizedPath } from '~/constants/appLocale'
import { blogCategoryLabel, blogCategoryTitle, blogHomePath, blogLocaleFor, blogPostPath, formatBlogDate, type BlogLocale } from '~/constants/blog'
import { GUIDE_FOR_BLOG_CATEGORY } from '~/constants/guidePages'
import type { SofaxCrumb } from '~/components/sofax/Breadcrumb.vue'
import type { BlogListResponse, BlogPost, BlogPostListItem } from '~/types'

/**
 * A blog article: pages/blog/[slug].vue for the English blog and
 * pages/cs-blog/[slug].vue and pages/de-blog/[slug].vue for the Czech and
 * German ones (constants/blog.ts). Each post
 * belongs to one blog; asked for under the other, it moves to its own URL.
 */
const props = withDefaults(defineProps<{ locale?: BlogLocale }>(), { locale: 'en' })

const home = blogHomePath(props.locale)
// Category pages exist for the English blog only.
const hasCategories = props.locale === 'en'

const route = useRoute()
const slug = computed(() => String(route.params.slug))

/**
 * Server-side fetch. This used to be a bare $fetch inside onMounted, which meant
 * the server rendered a spinner: no title, no description, no body, no JSON-LD.
 * Every article was invisible to crawlers.
 *
 * useAsyncData with an explicit $fetch rather than useFetch, because the 404
 * branch at the end of setup needs to inspect the error's status code.
 *
 * NOT awaited here: every composable below that touches the head needs the Nuxt
 * instance, and that context is lost after the first `await` in setup.
 * useAsyncData hands back its refs synchronously, so the head is wired to those
 * refs first and the promise is awaited last.
 */
const articleRequest = useAsyncData(
  () => `blog-post-${slug.value}`,
  () => $fetch<BlogPost>(`/api/blog/${encodeURIComponent(slug.value)}`),
  { watch: [slug] },
)
const { data: post, error } = articleRequest

/**
 * Sidebar "Recent posts". Fetched from the lightweight listing endpoint (no
 * `content` column) and, like the article, registered before any await so it
 * still runs inside the Nuxt context. One extra row is requested so the current
 * article can be dropped and three others still remain. A failure here must
 * never fail the page, so `default` swallows it into an empty list.
 */
const recentRequest = useAsyncData(
  () => `blog-recent-${props.locale}-${slug.value}`,
  () => $fetch<BlogListResponse>('/api/blog/list', { query: { limit: 4, locale: props.locale } }),
  {
    watch: [slug],
    default: () => [] as BlogPostListItem[],
    transform: (response: BlogListResponse) =>
      response.items.filter(item => item.slug !== slug.value).slice(0, 3),
  },
)
const recentPosts = computed(() => recentRequest.data.value ?? [])

const categoryLabel = computed(() => (hasCategories ? blogCategoryLabel(post.value?.category) : null))
const categoryGuide = computed(() => GUIDE_FOR_BLOG_CATEGORY[post.value?.category ?? ''])

const formatDate = (dateString: string) => formatBlogDate(dateString, props.locale)

const { t } = useI18n()
const { show } = useSnackbar()

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
    show(t('blog.linkCopied'))
  } catch (error) {
    console.error('Failed to copy link:', error)
    show('Failed to copy link')
  }
}

/** Search always lands on the global listing, the same as the listing's own field. */
const searchBlog = (term: string) =>
  navigateTo({ path: home, query: term ? { search: term } : {} })

const { siteUrl, absUrl } = useSiteUrl()
const canonical = computed(() => `${siteUrl}${blogPostPath({ slug: slug.value, locale: props.locale })}`)
const description = computed(
  () => stripQuotes(post.value?.meta_description || post.value?.description) || t('blog.metaDescription'),
)

/* Visible trail, built from the same names as the BreadcrumbList JSON-LD below. */
const crumbs = computed<SofaxCrumb[]>(() => {
  const article = post.value
  if (!article) return []
  return [
    { name: t('footer.blog'), to: home },
    ...(article.category && hasCategories
      ? [{ name: blogCategoryTitle(article.category)!, to: `/blog/category/${article.category}` }]
      : []),
    { name: cleanTitle(article.title) },
  ]
})

useSeoMeta({
  // No ' | InMyResume Blog' suffix here — app.vue's titleTemplate appends the
  // brand exactly once. Having both produced 'Title | InMyResume | InMyResume'.
  title: () => post.value?.meta_title || post.value?.title,
  description: () => description.value,
  ogTitle: () => cleanTitle(post.value?.meta_title || post.value?.title),
  ogDescription: () => description.value,
  ogType: 'article',
  ogUrl: () => canonical.value,
  ogSiteName: 'InMyResume',
  // Absolute URL, and undefined rather than '' when there is no image so unhead
  // drops the tag instead of emitting content="".
  ogImage: () => absUrl(post.value?.image_url) ?? absUrl('/og/blog.png'),
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: () => cleanTitle(post.value?.title),
  twitterCard: 'summary_large_image',
  articlePublishedTime: () => post.value?.published_at || post.value?.created_at,
  articleModifiedTime: () => post.value?.updated_at || post.value?.created_at,
})

useHead(() => ({
  // `key` replaces app.vue's site-wide canonical rather than adding a second one.
  link: [{ rel: 'canonical', key: 'canonical', href: canonical.value }],
  // One <meta> per tag. The previous single comma-joined tag was not the format
  // consumers expect.
  meta: (post.value?.tags ?? []).map(tag => ({ property: 'article:tag', content: tag })),
}))

/**
 * Structured data, emitted synchronously in setup.
 *
 * This used to sit in a watchEffect, which never ran with data on the server (so
 * articles shipped no JSON-LD at all) and leaked a duplicate <script> tag on
 * every re-run on the client, because useHead only binds to the component
 * lifecycle on its first synchronous call.
 */
const { articleNode, imageNode, breadcrumbNode, webPageNode, organizationNode, webSiteNode, useJsonLdGraph }
  = useJsonLd()

useJsonLdGraph(() => {
  const article = post.value
  if (!article) return []
  const path = blogPostPath(article)
  return [
    organizationNode(),
    webSiteNode(),
    webPageNode({
      path,
      name: cleanTitle(article.title),
      description: article.meta_description || article.description,
      hasBreadcrumb: true,
      primaryImage: article.image_url,
    }),
    breadcrumbNode(path, [
      { name: t('breadcrumbs.home'), path: localizedPath('/', props.locale) },
      { name: t('footer.blog'), path: home },
      ...(article.category && hasCategories
        ? [{ name: blogCategoryTitle(article.category)!, path: `/blog/category/${article.category}` }]
        : []),
      { name: cleanTitle(article.title) },
    ]),
    articleNode(article),
    imageNode(path, article.image_url),
  ]
})
/*
 * Head registration is complete, so it is now safe to await the fetch and turn
 * its outcome into an HTTP status. Distinguishing 404 from 502 matters: answering
 * 404 to a Supabase outage would tell Google to drop every real article. The old
 * code rendered a "not found" page with HTTP 200 — a soft 404 on every bad slug.
 */
await articleRequest

if (!post.value) {
  const statusCode = (error.value as { statusCode?: number } | null)?.statusCode
  throw createError({
    statusCode: statusCode === 404 ? 404 : 502,
    statusMessage: statusCode === 404 ? 'Blog post not found' : 'Failed to load blog post',
    fatal: true,
  })
}

// A post asked for under another language's blog (/blog/<Czech slug>, or a
// language switch) has a single URL of its own.
if (blogLocaleFor(post.value.locale) !== props.locale) {
  await navigateTo(blogPostPath(post.value), { redirectCode: 301 })
}

// Sidebar data is awaited last so it is in the server HTML but can never turn
// a healthy article into an error page (its failures resolve to an empty list).
await recentRequest
</script>

<style scoped>
/*
 * Everything typographic comes from `.rich-text-block`. The only local rule
 * keeps embedded media (videos, iframes pasted into the CMS) inside the column.
 */
:deep(.blog-content iframe),
:deep(.blog-content video) {
  @apply my-8 max-w-full rounded-[0.625rem];
}
</style>
